<script setup>
import { computed, onMounted, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import ButtonLink from '../../components/js/Components/UI/Button/ButtonLink.vue'
import BlockContent from '../../components/js/Components/UI/Content/BlockContent.vue'
import MainContainer from '../../components/js/Components/UI/Content/MainContainer.vue'
import PageLayout from '../../components/js/Components/UI/PageLayout.vue'
import DataTable from '../../components/js/Components/UI/Table/DataTable.vue'
import TableMenu from '../../components/js/Components/UI/Table/TableMenu.vue'
import useApi from '../../components/js/Composables/useApi'
import useContractExport from '../../components/js/Composables/useContractExport'
import useSearchParams from '../../components/js/Composables/useSearchParams'
import useToast from '../../components/js/Composables/useToast'
import useConfig from '../../Composables/useConfig'
import useUser from '../../Composables/User/useUser.ts'
import { Routes } from '../../Constants/Routes.ts'

const { t } = useI18n()
const { getJSON } = useApi()
const { addError } = useToast()
const { isTheme, isAdmin } = useConfig()
const { user, isPlatformClient, hasRole } = useUser()
const { searchParams } = useSearchParams()
const { exportContracts, canDownloadExport } = useContractExport()

const selectedStatus = ref(parseInt(searchParams.get('s') ?? 0))
const contractList = ref(null)

const filters = ref({
  units: [],
  platformSalesmen: [],
  suppliers: [],
  clients: [],
  refinancers: [],
})

const filtersValues = ref({
  unit: null,
  platformSalesman: null,
  supplier: null,
  client: null,
  refinancer: null,
  purchaseOrderNumber: null,
})
const contractListRequest = ref(null)

const submenuLabels = computed(() => [
  { id: 0, name: t('contract.list.type.status.ALL') },
  { id: 1, name: t('contract.list.type.status.PAID') },
  { id: 2, name: t('contract.list.type.status.FINISH') },
  { id: 3, name: t('contract.list.type.status.ARCHIVE') },
])

const showDownloadExportButton = computed(() => {
  return canDownloadExport(user.value?.roles || [])
})

const tableFilters = computed(() => {
  const refinancersCount = filters.value.refinancers?.length ?? 0
  return [
    { name: 'client', optionsKey: 'clients', domain: 'simulation', labelTop: true, condition: true },
    { name: 'supplier', optionsKey: 'suppliers', domain: 'simulation', labelTop: true, condition: true },
    { name: 'unit', optionsKey: 'units', domain: 'simulation', labelTop: true, condition: true },
    {
      name: 'platformSalesman',
      optionsKey: 'platformSalesmen',
      domain: 'simulation',
      labelTop: true,
      condition: !isTheme('lbp') || !isPlatformClient.value,
    },
    {
      name: 'refinancer',
      optionsKey: 'refinancers',
      domain: 'simulation',
      labelTop: true,
      condition: refinancersCount > 1,
    },
    {
      name: 'purchaseOrderNumber',
      domain: 'simulation',
      labelTop: true,
      condition: isTheme('ciscar'),
    },
  ].filter(({ condition }) => condition === true)
})

const columns = computed(() => {
  if (isTheme('hexapage')) {
    // TODO : create system to custom order/display column by clients
    return [
      { name: 'id', displayable: false },
      { name: 'clientId', displayable: false },
      { name: 'supplierId', displayable: false },
      { name: 'platformId', displayable: false },
      { name: 'accountCodeSupplier', displayable: false },
      {
        name: 'number',
        title: t('contract.list.table.labelNumber'),
        formatter: 'context',
        options: { renderType: 'portefeuille', renderId: 'id' },
      },
      {
        name: 'clientInfo',
        title: t('main.client'),
        formatter: 'entity',
        options: { render: '', renderId: 'clientId' },
      },
      {
        name: 'clientSiren',
        title: t('contract.list.table.sirenClient'),
        formatter: 'entity',
        options: { render: '', renderId: 'clientId' },
      },
      {
        name: 'endAt',
        title: t('contract.list.table.endAt'),
        formatter: 'date',
      },
      {
        name: 'createdAt',
        title: t('contract.list.table.labelCreatedAt'),
        formatter: 'date',
      },
      {
        name: 'refinancerName',
        title: t('contract.list.table.refinancerName'),
        style: { width: '120px;' },
      },
      {
        name: 'refinancerNumber',
        title: t('contract.list.table.refinancerNumber'),
      },
      {
        name: 'status',
        title: t('contract.list.table.labelStatus'),
        formatter: 'status',
        options: { renderType: 'contract' },
        style: { width: '120px;', className: 'text-center' },
      },
    ]
  }
  return [
    { name: 'id', displayable: false },
    { name: 'clientId', displayable: false },
    { name: 'supplierId', displayable: false },
    { name: 'platformId', displayable: false },
    { name: 'accountCodeSupplier', displayable: false },
    {
      name: 'createdAt',
      title: t('contract.list.table.labelCreatedAt'),
      formatter: 'date',
    },
    {
      name: 'number',
      title: t('contract.list.table.labelNumber'),
      formatter: 'context',
      options: { renderType: 'portefeuille', renderId: 'id' },
    },
    {
      name: 'refinancerNumber',
      title: t('contract.list.table.refinancerNumber'),
      displayable: isTheme('lbp'),
    },
    {
      name: 'agreementNumber',
      title: t('contract.list.table.demandNumber'),
      style: { className: 'col-align-center' },
      displayable: isTheme('financia'),
    },
    {
      name: 'accountCodeClient',
      title: t('contract.list.table.accountCodeClient'),
      displayable: isTheme('ciscar'),
    },
    {
      name: 'clientInfo',
      title: t('main.client'),
      formatter: 'entity',
      options: { render: '', renderId: 'clientId' },
    },
    {
      name: 'supplierInfo',
      title: t('main.supplier'),
      displayable: isAdmin(),
      formatter: 'entity',
      options: { render: '', renderId: 'supplierId' },
    },
    {
      name: 'platformName',
      title: t('contract.list.table.platformName'),
      formatter: 'entity',
      options: { render: '', renderId: 'platformId' },
      displayable: !isTheme('financia', 'lbp'),
    },
    {
      name: 'companySalesmanName',
      title: t('contract.list.table.companySalesmanName'),
      displayable: !isTheme('financia'),
    },
    {
      name: 'platformSalesman',
      title: t('contract.list.table.labelPlatformSalesman'),
    },
    {
      name: 'rentMonthly',
      title: t('contract.list.table.labelRentMonthly'),
      type: 'num-fmt',
      displayable: !isTheme('ciscar'),
      formatter: 'currency',
      style: { width: '60px;' },
    },
    {
      name: 'amount',
      title: t('contract.list.table.labelTotalAmount'),
      type: 'num-fmt',
      displayable: !isTheme('ciscar'),
      formatter: 'currency',
      style: { width: '60px;' },
    },
    {
      name: 'rentAmount',
      title: t('contract.list.table.labelRentAmount'),
      displayable: isTheme('ciscar'),
      formatter: 'currency',
      style: { width: '60px;' },
    },
    {
      name: 'status',
      title: t('contract.list.table.labelStatus'),
      formatter: 'status',
      options: { renderType: 'contract' },
      style: { width: '120px;', className: 'text-center' },
    },
    {
      name: 'startAt',
      title: t('contract.list.table.startAt'),
      formatter: 'date',
      displayable: isTheme('financia'),
    },
    {
      name: 'endAt',
      title: t('contract.list.table.endAt'),
      formatter: 'date',
      displayable: isTheme('financia'),
    },
    {
      name: 'transferAt',
      title: t('contract.list.table.transferAt'),
      formatter: 'date',
      displayable: isTheme('financia'),
    },
    { name: 'paidAt', title: t('contract.list.table.labelPaidAt'), formatter: 'date' },
    {
      name: 'esign',
      title: t('contract.list.table.esign'),
      formatter: 'boolean',
      displayable: isTheme('ciscar', 'olinn', 'financia'),
      options: { render: '' },
      style: { width: '60px;' },
    },
    {
      name: 'formerContractNumber',
      title: t('contract.list.table.formerContractNumber'),
      displayable: isTheme('ciscar', 'olinn'),
      style: { width: '60px;' },
    },
    {
      name: 'deliveryLabel',
      title: t('contract.list.table.labelDeliveryAddress'),
      displayable: isTheme('ciscar'),
      style: { width: '120px;' },
    },
    {
      name: 'refinancerName',
      title: t('contract.list.table.refinancerName'),
      displayable:
        (isTheme('ciscar', 'olinn', 'financia') && (user.value?.isBusinessMan || user.value?.isAdmin)) ||
        (isTheme('financia') && user.value?.isPlatformSalesman),
      style: { width: '120px;' },
    },
    {
      name: 'contractMode',
      title: t('contract.list.table.contractMode'),
      style: { width: '120px;' },
      displayable: isTheme('lbp'),
    },
  ]
})

const cancelPendingRequest = () => {
  contractListRequest.value?.cancel()
  contractListRequest.value = null
}

const getContracts = async (callback, { page, limit, search, sort = [] }) => {
  cancelPendingRequest()

  const newUrlSearchParams = new URLSearchParams(searchParams.toString())
  newUrlSearchParams.set('page', page)
  newUrlSearchParams.set('limit', limit)

  for (const { field, order } of sort) {
    newUrlSearchParams.set(`sort[${field}]`, order)
  }

  if (search) {
    newUrlSearchParams.set('q', search)
  }

  searchParams.value = newUrlSearchParams
  const getJsonPromise = getJSON(`v2/contracts?${newUrlSearchParams.toString()}`)
  contractListRequest.value = getJsonPromise
  const { items: contracts, total } = await getJsonPromise
  callback({ data: contracts, recordsTotal: total, recordsFiltered: total })
}

const setSelectedStatus = (value) => {
  value = parseInt(value)
  if (value !== selectedStatus.value) {
    selectedStatus.value = value
    updateParams()
  }
}

const loadFilterOptions = async () => {
  try {
    const options = await getJSON(`contract/filter-options?${searchParams.toString()}`)
    filters.value = options
  } catch {
    addError(t('errors.search.filter_options_fetch_failed'))
  }
}

const updateFilterData = async (filters) => {
  cancelPendingRequest()
  for (const [name, value] of Object.entries(filters)) {
    if (value && (typeof value === 'string' || value > 0)) {
      searchParams.set(name, value)
    } else {
      searchParams.delete(name)
    }
  }

  contractList.value?.renderDataTable()
  await loadFilterOptions()
}

const setDateTable = async (createdAtValue, createdAtEndValue) => {
  cancelPendingRequest()
  if (createdAtValue) {
    searchParams.set('createdAt', createdAtValue)
  } else {
    searchParams.delete('createdAt')
  }
  if (createdAtEndValue) {
    searchParams.set('createdAtEnd', createdAtEndValue)
  } else {
    searchParams.delete('createdAtEnd')
  }
  contractList.value?.renderDataTable()
  await loadFilterOptions()
}

const updateParams = async () => {
  cancelPendingRequest()
  let statuses = []
  switch (selectedStatus.value) {
    case 1:
      statuses.push('PAID')
      break
    case 2:
      statuses.push('FINISH')
      break
    case 3:
      statuses.push('ENDED', 'CLOSED', 'PROLONGATION', 'CESSION')
      break
    default:
      statuses.push('PAID', 'FINISH', 'ENDED', 'CLOSED', 'PROLONGATION', 'CESSION')
  }
  searchParams.set('status', statuses.join('-'))
  contractList.value?.renderDataTable()
  await loadFilterOptions()
}

const download = async () => {
  await exportContracts('contracts/export', searchParams)
}

onMounted(async () => {
  await updateParams()
  await loadFilterOptions()
})
</script>
<template>
  <PageLayout menu="portefeuille-index">
    <MainContainer title-t="menu.myPortfolio">
      <BlockContent>
        <TableMenu
          :status="selectedStatus"
          :show-calendar-filter="true"
          :filters="tableFilters"
          :filter-options="filters"
          :filter-values="filtersValues"
          :submenu-labels="submenuLabels"
          :show-download-button="showDownloadExportButton"
          endpoint="contract"
          @set-selected-status="setSelectedStatus"
          @download="download"
          @update-filter-data="updateFilterData"
          @set-date="setDateTable"
        >
          <template #right>
            <ButtonLink
              v-if="hasRole('ROLE_RESTRUCTURING')"
              type="add"
              external
              label="restructuring.actions.access"
              :href="{ name: Routes.RESTRUCTURING_LIST }"
            />
          </template>
        </TableMenu>
        <DataTable
          id="listeContrat"
          ref="contractList"
          :columns="columns"
          :fetch="getContracts"
          :order="[{ dir: 'desc', name: 'createdAt' }]"
          :first-load="false"
          pagination-mode="server"
        >
        </DataTable>
      </BlockContent>
    </MainContainer>
  </PageLayout>
</template>
